import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, X, Check } from 'lucide-react';
import { Card } from '../ui/card';

export const GeneralAppsArticle = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      question: "Why don't regular time-tracking apps work for construction?",
      answer: "They lack overtime logic, per diem features, multi-rate support, and aren't built for harsh jobsite conditions. They're designed for office work, not trades."
    },
    {
      question: "What makes PayFlow different from other time apps?",
      answer: "PayFlow is purpose-built for construction and refinery work with support for complex OT rules, per diem tracking, multiple pay rates, and paycheck estimation."
    },
    {
      question: "Can I use a free time-tracking app for construction work?",
      answer: "You can track basic hours, but you won't get accurate pay estimates, OT calculations, or per diem tracking. For paycheck clarity, you need a trades-specific tool like PayFlow."
    },
    {
      question: "Does PayFlow work offline on jobsites?",
      answer: "PayFlow is designed to handle spotty connectivity and sync data when connection is restored, making it reliable for remote jobsite conditions."
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Why General Time-Tracking Apps Fail Tradespeople",
    "description": "Explains why office-oriented time tracking tools fail in construction and refinery environments, and how PayFlow solves pay visibility for tradespeople.",
    "author": {
      "@type": "Organization",
      "name": "ProjectFlow Automations"
    },
    "keywords": "construction time tracking, refinery payroll tools, per diem tracking, overtime app, PayFlow vs generic apps",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://projectflowautomations.com/knowledge/general-apps-vs-payflow"
    }
  };

  return (
    <>
      <Helmet>
        <title>Why Generic Time-Tracking Apps Fail Trades | PayFlow</title>
        <meta name="description" content="Learn why general time-tracking apps don't work for construction and refinery workers, and why PayFlow is purpose-built for OT, per diem, and multi-rate pay." />
        <meta name="keywords" content="time tracking for construction, Clockify alternative trades, refinery time tracking, OT tracking app, per diem tracking, PayFlow vs general apps" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#0D1115] pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <a href="/knowledge" className="inline-flex items-center text-[#30D5C8] hover:text-[#30D5C8]/80 font-['Open_Sans'] mb-8 transition-colors">
            <ArrowLeft size={20} className="mr-2" />
            Back to Knowledge Center
          </a>

          <div className="mb-12">
            <div className="inline-block bg-[#30D5C8]/10 border border-[#30D5C8]/30 rounded-full px-4 py-2 mb-6">
              <span className="text-[#30D5C8] text-sm font-['Montserrat'] font-semibold">APP COMPARISON</span>
            </div>
            <h1 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-6 leading-tight">
              Why General Time-Tracking Apps Fail Tradespeople
            </h1>
            <p className="text-[#CBD3D9] text-xl font-['Open_Sans'] leading-relaxed">
              Most time-tracking apps were built for office jobs — not construction, not shutdowns, and not refinery work. They simply cannot handle the complexity of the trades.
            </p>
          </div>

          <article className="space-y-8">
            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">The Limitations of Generic Apps</h2>
              
              <div className="space-y-6">
                <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                  <div className="flex items-start space-x-4">
                    <X className="text-red-500 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">1. No Real Overtime Logic</h3>
                      <p className="text-[#CBD3D9] font-['Open_Sans'] mb-4">They assume:</p>
                      <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
                        <li className="flex items-start"><span className="text-[#506273] mr-3">•</span><span>8-hour days</span></li>
                        <li className="flex items-start"><span className="text-[#506273] mr-3">•</span><span>Fixed schedules</span></li>
                        <li className="flex items-start"><span className="text-[#506273] mr-3">•</span><span>Simple weekly totals</span></li>
                      </ul>
                      <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold mt-4">Trades don't work that way.</p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                  <div className="flex items-start space-x-4">
                    <X className="text-red-500 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">2. No Per Diem Features</h3>
                      <p className="text-[#CBD3D9] font-['Open_Sans']">
                        Most time apps don't understand the per diem system at all.
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                  <div className="flex items-start space-x-4">
                    <X className="text-red-500 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">3. Not Built for Multiple Rates</h3>
                      <p className="text-[#CBD3D9] font-['Open_Sans'] mb-4">Shutdown workers often work:</p>
                      <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
                        <li className="flex items-start"><span className="text-[#506273] mr-3">•</span><span>Straight time</span></li>
                        <li className="flex items-start"><span className="text-[#506273] mr-3">•</span><span>OT</span></li>
                        <li className="flex items-start"><span className="text-[#506273] mr-3">•</span><span>DT</span></li>
                        <li className="flex items-start"><span className="text-[#506273] mr-3">•</span><span>Premium shifts</span></li>
                        <li className="flex items-start"><span className="text-[#506273] mr-3">•</span><span>Multiple job codes</span></li>
                      </ul>
                      <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold mt-4">Generic apps can't track that accurately.</p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                  <div className="flex items-start space-x-4">
                    <X className="text-red-500 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">4. Not Built for Harsh or Remote Jobsites</h3>
                      <p className="text-[#CBD3D9] font-['Open_Sans']">
                        Spotty service, quick shift changes, and long hours break most office-oriented apps.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">Why a Trades-Specific App Is Necessary</h2>
              <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#30D5C8]/30 p-8">
                <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg mb-6">Construction workers need:</p>
                <ul className="space-y-3 text-[#CBD3D9] font-['Open_Sans'] text-lg">
                  <li className="flex items-start"><span className="text-[#30D5C8] mr-3"><Check size={20} /></span><span>Field-ready tools</span></li>
                  <li className="flex items-start"><span className="text-[#30D5C8] mr-3"><Check size={20} /></span><span>Simple pay breakdowns</span></li>
                  <li className="flex items-start"><span className="text-[#30D5C8] mr-3"><Check size={20} /></span><span>Accurate OT rules</span></li>
                  <li className="flex items-start"><span className="text-[#30D5C8] mr-3"><Check size={20} /></span><span>Per diem tracking</span></li>
                  <li className="flex items-start"><span className="text-[#30D5C8] mr-3"><Check size={20} /></span><span>Clear paycheck predictions</span></li>
                </ul>
                <p className="text-[#F5F7F8] font-['Montserrat'] font-bold text-xl mt-6">
                  PayFlow fills the gap those apps were never designed to address.
                </p>
              </Card>
            </section>

            <Card className="bg-gradient-to-br from-[#30D5C8]/20 to-[#30D5C8]/5 border-[#30D5C8]/50 p-8">
              <p className="text-[#F5F7F8] font-['Open_Sans'] text-lg leading-relaxed mb-6">
                PayFlow by ProjectFlow Automations is built for construction and refinery workers who want clarity on hours, overtime, and per diem before payday.
              </p>
              <a href="/payflow" className="inline-block bg-[#30D5C8] text-[#0D1115] hover:bg-[#30D5C8]/90 font-['Montserrat'] font-bold text-lg px-8 py-4 rounded-lg transition-colors">
                Learn More About PayFlow
              </a>
            </Card>
          </article>

          <section className="mt-16">
            <h2 className="font-['Montserrat'] font-bold text-3xl text-[#F5F7F8] mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-6">
                  <h3 className="font-['Montserrat'] font-bold text-lg text-[#30D5C8] mb-3">{faq.question}</h3>
                  <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">{faq.answer}</p>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};